// Экран «данные не загрузились» (Алексей 26.10 #2635, #2639): не красный текст, а спокойный экран
// в стиле «режим „Все филиалы“ недоступен» — и честно о причине:
//   • офлайн — «Кажется, вы не в сети», данные подтянутся сами по событию online;
//   • сервер не отвечает (редеплой бэкенда) — «Сервер обновляется», прогресс от начала сборки и робот;
//     когда сервер ответил — робот улетает, данные перезапрашиваются сами (lib/serverHealth);
//   • иначе — «Не получилось подключиться к серверу, попробуйте обновить страницу через минуту»,
//     с роботом вместо иконки (#2639) и контактом.
// С 29.10 (#3224) текст говорит ОДНА плашка в каркасе (layout/ServerStatusBar). Пока она видна, этот
// экран в секциях — тихая заглушка без текста, на всю страницу — только робот: без «Не удалось…»
// по пять раз. «Сервер обновляется» — только если монитор деплоя сказал, что идёт деплой; иначе честно
// «сервер не отвечает».
import { useEffect, useRef, useState } from 'react';
import { WifiOff } from 'lucide-react';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import {
  useServerHealth, useGlobalNoticeActive, isConnectivityError, retryNow, type ServerHealth,
  DEFAULT_DEPLOY_MS, FALLBACK_DOWN_MS, DOWN_UNKNOWN_AFTER_MS, deployActive,
} from '@/lib/serverHealth';
import { WaitingRobot } from '@/components/common/WaitingRobot';

/** Куда писать, если не проходит. Один хэндл на все экраны. */
export const SUPPORT_TG = 'alexeysdk';

export type View = 'offline' | 'checking' | 'updating' | 'noResponse' | 'back' | 'unknown';

export function viewOf(h: ServerHealth, error: unknown, now: number): View {
  if (h.kind === 'offline') return 'offline';
  if (h.kind === 'checking') return 'checking';
  if (h.kind === 'back') return 'back';
  if (h.kind === 'down') {
    const d = h.deploy;
    if (deployActive(d, now)) return 'updating';
    return now - h.since < DOWN_UNKNOWN_AFTER_MS ? 'noResponse' : 'unknown';
  }
  if (isConnectivityError(error) && typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
  return 'unknown';
}

/** 0…1: линейно до 90 % к ожидаемому концу, дальше медленно ползёт к 99 % — не стоит и не «готово». */
export function progressOf(h: ServerHealth, now: number): number {
  if (h.kind === 'back') return 1;
  if (h.kind !== 'down') return 0;
  const d = deployActive(h.deploy, now) ? h.deploy : null;
  const start = d ? Date.parse(d.started_at) : h.since;
  const expected = d ? (d.expected_seconds ? d.expected_seconds * 1000 : DEFAULT_DEPLOY_MS) : FALLBACK_DOWN_MS;
  const r = Math.max(0, (now - start) / expected);
  return r <= 1 ? 0.9 * r : 0.9 + 0.09 * (1 - Math.exp(-(r - 1) * 1.5));
}

export function ServerStatusNotice({ error, onRetry, variant = 'page', className }: {
  /** Ошибка запроса страницы — нужна, чтобы отличить офлайн, когда общий статус ещё «ок». */
  error?: unknown;
  /** Перезапрос этой страницы; по умолчанию — перезапрос всего кэша SWR. */
  onRetry?: () => void;
  /** page — на всю высоту контента; card — внутри карточки/секции. */
  variant?: 'page' | 'card';
  className?: string;
}) {
  const { t } = useT();
  const health = useServerHealth();
  const quiet = useGlobalNoticeActive();
  const [now, setNow] = useState(() => Date.now());
  const view = viewOf(health, error, now);
  const ticking = health.kind === 'down';
  useEffect(() => {
    if (!ticking) return;
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [ticking]);

  // Робот улетает и при «снова на связи», и перед ручным повтором; если после этого экран не исчез
  // (повтор не помог), прилетает новый — с анимацией, а не «из воздуха».
  const [retrying, setRetrying] = useState(false);
  const [gen, setGen] = useState(0);
  const gone = useRef(false);
  const leaving = view === 'back' || retrying;
  useEffect(() => {
    if (!leaving && gone.current) { gone.current = false; setGen((g) => g + 1); }
  }, [leaving]);
  const retryTimer = useRef<number | null>(null);
  useEffect(() => () => { if (retryTimer.current != null) window.clearTimeout(retryTimer.current); }, []);
  const onGone = () => {
    gone.current = true;
    if (!retrying) return;
    (onRetry ?? retryNow)();
    retryTimer.current = window.setTimeout(() => setRetrying(false), 1500);
  };

  const page = variant === 'page';
  const robotH = page ? 96 : 60;
  const progress = progressOf(health, now);
  const slow = view === 'updating' && progress > 0.9;

  const title = {
    offline: t('serverStatus.offline.title'),
    checking: t('serverStatus.checking.title'),
    updating: t('serverStatus.updating.title'),
    noResponse: t('serverStatus.noResponse.title'),
    back: t('serverStatus.back.title'),
    unknown: t('dataError.title'),
  }[view];

  // Плашка наверху уже всё сказала: секция — пустая рамка, страница — робот без текста.
  if (quiet) {
    if (!page) {
      return (
        // Скелетон, а не значок обновления в пустой рамке (Алексей 04.11 #4055: «странный рефреш-знак»).
        <div
          role="status"
          aria-label={t('serverStatus.quiet')}
          aria-busy="true"
          className={cn('min-h-16 w-full animate-pulse rounded-md bg-muted', className)}
        />
      );
    }
    return (
      <div role="status" aria-label={title} className={cn('mx-auto flex min-h-[calc(100vh-12rem)] w-full max-w-2xl items-center justify-center px-4', className)}>
        <WaitingRobot key={gen} height={robotH} leaving={leaving} onGone={onGone} className="max-w-md" />
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'mx-auto flex w-full flex-col items-center justify-center text-center',
        page ? 'min-h-[calc(100vh-8rem)] max-w-2xl gap-3 px-4 py-12' : 'max-w-xl gap-2 px-3 py-5',
        className,
      )}
    >
      {view === 'offline' ? (
        <span className={cn('flex items-center justify-center rounded-xl bg-muted text-muted-foreground', page ? 'size-12' : 'size-10')}>
          <WifiOff className={page ? 'size-6' : 'size-5'} />
        </span>
      ) : (
        <WaitingRobot key={gen} height={robotH} leaving={leaving} onGone={onGone} className="max-w-md" />
      )}
      <h2 className={cn('text-balance font-semibold tracking-tight text-foreground', page ? 'text-xl' : 'text-base')}>
        {title}
      </h2>

      {view === 'offline' && (
        <p className="max-w-xl text-balance text-sm text-muted-foreground">{t('serverStatus.offline.hint')}</p>
      )}

      {view === 'noResponse' && (
        <p className="max-w-xl text-balance text-sm text-muted-foreground">{t('serverStatus.noResponse.hint')}</p>
      )}

      {(view === 'updating' || view === 'back') && (
        <>
          <div className="h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-muted" aria-hidden>
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-500 ease-linear"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
          {view === 'updating' && (
            <p className="max-w-xl text-balance text-sm text-muted-foreground">
              {t(slow ? 'serverStatus.updating.slow' : 'serverStatus.updating.hint')}
            </p>
          )}
        </>
      )}

      {view === 'unknown' && (
        <>
          <p className="max-w-xl text-balance text-sm text-muted-foreground">
            {t('dataError.retryHint')}{' '}
            {t('dataError.contactPrefix')}{' '}
            <a
              href={`https://t.me/${SUPPORT_TG}`}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              @{SUPPORT_TG}
            </a>
            .
          </p>
        </>
      )}
    </div>
  );
}

export default ServerStatusNotice;
